import type { TypedWrappedFieldProps, WrappedFieldProps } from "redux-form";
import { Field, Fields, formValueSelector } from "redux-form";

import {
  Box,
  Button,
  Stack,
  Switch,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { LongArrowLeftIcon } from "@braintrust/braintrust-ui-components/Icons";
import { useIsNodeOrImpersonated } from "@js/apps/common/hooks/use-is-node-or-impersonated";
import { usePostJobButton } from "@js/apps/employer/hooks/post-job-button";
import { goBackToSecondJobScreen } from "@js/apps/jobs/actions";
import { ReviewBeforeSendingSummaryBox } from "@js/apps/jobs/components/review-before-sending";
import { useGetJobFlowEntry } from "@js/apps/jobs/hooks/use-get-job-flow-entry";
import { Modal } from "@js/components/modal";
import { SwitchField } from "@js/forms/fields/switch";
import { useAppDispatch, useAppSelector } from "@js/hooks";
import type { JobFormValues } from "@js/types/jobs";

import { CREATE_JOB_FORM_ID, EDIT_JOB_FORM_ID } from "../../constants";

import { NonSupportedCountriesWarningPanel } from "./non-supported-countries-warning-panel";
import {
  StatusForAiInterview,
  StatusForApplicationQuestions,
  StatusForCategory,
  StatusForExperience,
  StatusForJobDescription,
  StatusForProjectDetails,
  StatusForRate,
  StatusForSkills,
  StatusForTitle,
} from "./statuses";

import styles from "./style.module.scss";

const PostJobButton = () => {
  const { handleBtnClick, disabled } = usePostJobButton();

  return (
    <Button
      onClick={handleBtnClick}
      variant="positive"
      type="submit"
      shape="squared"
      disabled={disabled}
    >
      Post job
    </Button>
  );
};

type SummarizeJobModalContentProps = StatusForModalFieldsProps;

export const SummarizeJobModalContent = ({
  values,
  nonSupportedLocationWarnings,
}: SummarizeJobModalContentProps) => {
  const warningCount = nonSupportedLocationWarnings?.length ?? 0;

  return (
    <Box>
      <SummarizeModalHeader />
      <Stack
        sx={{
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 3, md: 5, lg: 6 },
          rowGap: 5,
          background: "#FAFAFA",
        }}
      >
        <Box className={styles.container}>
          <Box className={styles.header}>
            <Typography
              component="p"
              className="dot-prefix"
              color="dark-violet"
              fontWeight={500}
              sx={{
                fontSize: "11px !important",
                "--dot-size": "6px",
                textTransform: "uppercase",
                letterSpacing: "1.2px !important",
                mb: 0.75,
              }}
            >
              Step 2 of 2 · Final review
            </Typography>
            <Typography
              component="h4"
              fontWeight={500}
              variant="title"
              size="small"
            >
              Let’s make sure everything looks right
            </Typography>
            {warningCount > 0 && (
              <Typography
                component="p"
                variant="paragraph"
                size="small"
                sx={{ mt: 1, color: "var(--dark-grey)" }}
              >
                Review the job details below.{" "}
                {warningCount === 1
                  ? "One location"
                  : `${warningCount} locations`}{" "}
                flagged for payment review &ndash; see the panel on the right
                before posting.
              </Typography>
            )}
          </Box>
          <Box className={styles.status} sx={{ overflow: "hidden" }}>
            <StatusForModalFields
              values={values}
              nonSupportedLocationWarnings={nonSupportedLocationWarnings}
            />
          </Box>
          <Stack className={styles.annotations} sx={{ gap: 2 }}>
            <AutoInviteContainer />
            <SkipApprovalContainer />
            {warningCount > 0 && (
              <NonSupportedCountriesWarningPanel count={warningCount} />
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const SummarizeModalHeader = () => {
  const dispatch = useAppDispatch();
  const { flow_entry } = useGetJobFlowEntry();

  const handleBtnClick = () => {
    closeSummarizeJobModal();
    dispatch(goBackToSecondJobScreen({ flow_entry }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        py: 2.5,
        px: 4,
        borderBottom: "1px solid var(--grey-4)",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Button
        variant="transparent"
        shape="squared"
        onClick={handleBtnClick}
        startIcon={<LongArrowLeftIcon />}
      >
        Back
      </Button>

      <PostJobButton />
    </Box>
  );
};

type StatusForModalFieldsProps = {
  values: JobFormValues;
  nonSupportedLocationWarnings?: string[];
};

export const StatusForModalFields = ({
  values,
  nonSupportedLocationWarnings = [],
}: StatusForModalFieldsProps) => {
  return (
    <ReviewBeforeSendingSummaryBox sx={{ background: "var(--white)" }}>
      <StatusForTitle values={values} />
      <StatusForCategory values={values} />
      <StatusForProjectDetails
        values={values}
        nonSupportedLocationWarnings={nonSupportedLocationWarnings}
      />
      <StatusForSkills values={values} />
      <StatusForExperience values={values} />
      <StatusForRate values={values} />
      <StatusForJobDescription values={values} />
      <StatusForApplicationQuestions values={values} />
      <StatusForAiInterview values={values} />
    </ReviewBeforeSendingSummaryBox>
  );
};

type AutoInviteProps = {
  jobToEdit?: boolean;
};

export const AutoInviteContainer = ({ jobToEdit }: AutoInviteProps) => {
  return (
    <Field
      name="is_private"
      component={AutoInviteWrapper}
      jobToEdit={jobToEdit}
    />
  );
};

const AutoInviteWrapper = ({
  input,
  jobToEdit,
}: AutoInviteProps & WrappedFieldProps) => {
  const isJobPrivate = input.value;

  if (isJobPrivate) return null;
  return <AutoInviteContent jobToEdit={jobToEdit} />;
};

const AutoInviteContent = ({ jobToEdit }: AutoInviteProps) => {
  const formId = jobToEdit ? EDIT_JOB_FORM_ID : CREATE_JOB_FORM_ID;

  const selector = formValueSelector(formId);
  const autoInviteTalentOn: boolean = useAppSelector((state) =>
    selector(state, "auto_invite_talent"),
  );

  return (
    <div className={styles.toggleContainer}>
      <Box>
        <Field
          component={SwitchField}
          name="auto_invite_talent"
          label={
            <Typography component="span" size="small" fontWeight={500}>
              Auto invite {autoInviteTalentOn ? "on" : "off"}
            </Typography>
          }
          size="small"
          color="success"
        />
        <p className={styles.content}>
          We will automatically invite any relevant talent that looks like a
          good match for this job.
        </p>
      </Box>
    </div>
  );
};

export const SkipApprovalContainer = () => {
  return (
    <Fields
      names={["skip_approval_to_job", "employer_skip_approval_to_job"]}
      component={SkipApprovalField}
    />
  );
};

type SkipApprovalFieldProps = {
  skip_approval_to_job: TypedWrappedFieldProps<boolean | undefined>;
  employer_skip_approval_to_job: TypedWrappedFieldProps<boolean | undefined>;
};
export const SkipApprovalField = ({
  skip_approval_to_job,
  employer_skip_approval_to_job,
}: SkipApprovalFieldProps) => {
  const isNodeStaffOrImpersonated = useIsNodeOrImpersonated();

  if (!isNodeStaffOrImpersonated) {
    return null;
  }

  const employerSkipApprovalOn = !!employer_skip_approval_to_job.input.value;
  if (employerSkipApprovalOn) {
    return (
      <div className={styles.toggleContainer}>
        <Box>
          <Typography component="p" fontWeight={500} size="small">
            Skip approval on
          </Typography>
          <p className={styles.content}>
            Approval is not required for any job from this employer.
          </p>
        </Box>
      </div>
    );
  }

  const skipApprovalOn = !!skip_approval_to_job.input.value;

  return (
    <div className={styles.toggleContainer}>
      <Box>
        <Switch
          label={
            <Typography component="span" size="small" fontWeight={500}>
              Skip approval {skipApprovalOn ? "on" : "off"}
            </Typography>
          }
          checked={skipApprovalOn}
          size="small"
          color="success"
          onChange={(_e, checked) => {
            skip_approval_to_job.input.onChange(checked);
          }}
        />
        <p className={styles.content}>
          Applicants will not need approval or verification before proceeding.
        </p>
      </Box>
    </div>
  );
};

export const SummarizeJobModalInstance = Modal("summarize-job-post", {
  closeButton: false,
  padding: false,
  keepOnBackdropClick: true,
  disablePortal: true,
  className: styles.modal,
});

export const openSummarizeJobModal = (
  values: JobFormValues,
  nonSupportedLocationWarnings: string[] = [],
) => {
  SummarizeJobModalInstance.open({
    children: (
      <SummarizeJobModalContent
        values={values}
        nonSupportedLocationWarnings={nonSupportedLocationWarnings}
      />
    ),
  });
};

export const closeSummarizeJobModal = () => {
  SummarizeJobModalInstance.close();
};
